import type { ButtonProps } from '@packages/shared';
import { Box, Stack, Button, CircularProgress, Link } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { graphql, getFragmentData } from '@packages/gql/generated/shopping';

import { useIsBot } from '@packages/tracking';
import { useRouter } from 'next/router';
import { getVariationFormattedLink } from '../../../helpers';
import { getIsAvailable } from '../../../helpers/getIsAvailable/getIsAvailable';
import {
  defaultTileStyle,
  disabledTileStyle,
  selectedDisabledTileStyle,
  selectedTileStyle,
} from './styles';
import type { SetSelectedVariation } from '../../../types';

const TilesValuesFragment = graphql(/* GraphQL */ `
  fragment TilesValuesFragment on ProductDimension {
    values {
      displayText
      isSelected
      sku
      availability {
        state
      }
    }
  }
`);

export type TilesSelectionProps = {
  /**
   * The GraphQL dimension data for the given product
   */
  dimensionData: FragmentType<typeof TilesValuesFragment>;
  onChange: SetSelectedVariation;
  selectedVariation: string | null;
};

const TileButton = ({
  active,
  onClick,
  sku,
  children,
  selectedVariation,
  ...props
}: Omit<ButtonProps, 'onClick'> & {
  active: boolean;
  onClick: SetSelectedVariation;
  sku: string;
  selectedVariation: string | null;
}) => {
  const isLoading = selectedVariation === sku && !active;

  return (
    <Button
      {...props}
      onClick={() => {
        if (!active) {
          onClick(sku);
        }
      }}
    >
      {isLoading && (
        <CircularProgress
          sx={{ position: 'absolute', transform: 'translate(-50%, -50%)' }}
          size="s"
          color="secondary"
        />
      )}
      <Box
        sx={{
          padding: 1,
          ...(isLoading && { visibility: 'hidden' }),
        }}
      >
        {children}
      </Box>
    </Button>
  );
};

/**
 * TilesSelection to choose a product variation on Detailview
 * */
export const TilesSelection = ({
  dimensionData,
  onChange,
  selectedVariation,
}: TilesSelectionProps) => {
  const dimension = getFragmentData(TilesValuesFragment, dimensionData);
  const router = useRouter();
  const isBot = useIsBot();

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        gap={1.5}
        sx={{ mt: 0.75, button: { position: 'relative' } }}
      >
        {dimension.values.map(({ sku, displayText, availability, isSelected }) => {
          if (!sku) {
            return null;
          }
          const isAvailable = availability ? getIsAvailable(availability.state) : true;
          let style = defaultTileStyle;

          if (!isAvailable && !isSelected) {
            style = disabledTileStyle;
          } else if (isSelected && isAvailable) {
            style = selectedTileStyle;
          } else if (!isAvailable && isSelected) {
            style = selectedDisabledTileStyle;
          }

          return (
            <TileButton
              key={`tilebtn${sku}`}
              sku={sku}
              active={!!isSelected}
              sx={style}
              size="medium"
              onClick={onChange}
              LinkComponent={Link}
              href={isBot ? getVariationFormattedLink(sku, router) : undefined}
              selectedVariation={selectedVariation}
            >
              {displayText}
            </TileButton>
          );
        })}
      </Stack>
    </Box>
  );
};
